import { CartItemsComponent } from '@theme/component-cart-items';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Adds the optional mask and lets the existing cart components render Shopify's discount and totals. */
class SoleneCartMaskOffer extends HTMLElement {
  static #pending = false;

  connectedCallback() {
    this.querySelector('[data-add-mask]')?.removeAttribute('hidden');
    this.addEventListener('click', this.#addMask);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#addMask);
  }

  /** @param {MouseEvent} event */
  #addMask = async (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-add-mask]')) return;
    if (SoleneCartMaskOffer.#pending) return;
    SoleneCartMaskOffer.#pending = true;

    const errorText = this.querySelector('[data-offer-error]');
    if (errorText instanceof HTMLElement) errorText.hidden = true;

    // Hold checkout and other cart controls until the mutation is settled.
    const cartComponents = Array.from(document.querySelectorAll('cart-items-component'));
    const controls = cartComponents.flatMap((cart) => Array.from(cart.querySelectorAll('button, input')));
    const disabledStates = controls.map((control) => ({ control, disabled: control.hasAttribute('disabled') }));
    const accelerated = cartComponents.flatMap((cart) => Array.from(cart.querySelectorAll('.additional-checkout-buttons')));
    const inertStates = accelerated.map((element) => ({ element, inert: element.hasAttribute('inert') }));
    for (const control of controls) control.setAttribute('disabled', '');
    for (const element of accelerated) element.setAttribute('inert', '');
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-button-label]')?.setAttribute('hidden', '');
    this.querySelector('[data-button-pending]')?.removeAttribute('hidden');

    let added = false;
    try {
      // Finish quantity changes already underway before checking eligibility again.
      while (CartItemsComponent.pendingQuantityUpdates.length) {
        await Promise.all(CartItemsComponent.pendingQuantityUpdates);
      }

      const sectionIds = [...new Set(cartComponents.map((cart) => cart.getAttribute('data-section-id')).filter(Boolean))];
      const getCart = async () => {
        const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.dataset.errorText);
        return response.json();
      };
      let cart = await getCart();
      const maskInBag = cart.items.some((item) => String(item.variant_id) === this.dataset.variantId && item.quantity > 0);
      const apresInBag = cart.items.some((item) => item.handle === 'massage-glasses' && item.quantity > 0);

      let sections;
      if (apresInBag && !maskInBag && cart.currency === 'CAD') {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorText);
        added = true;
        sections = result.sections;
        cart = await getCart();
      }

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: added ? 'add' : 'update',
        context: 'cart',
        lines: added ? [{ merchandiseId: this.dataset.variantId, quantity: 1 }] : [],
        promise: deferred.promise,
      }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections, itemCount: cart.item_count, source: 'solene-cart-mask-offer', didError: false },
      });

      // The reminder is removed by the cart render. Keep focus on the next step.
      requestAnimationFrame(() => {
        const checkout = document.querySelector('cart-items-component .cart__checkout-button');
        if (checkout instanceof HTMLElement) checkout.focus({ preventScroll: true });
      });
    } catch (error) {
      // A successful add must never become a retry that adds a second mask.
      if (added) {
        window.location.reload();
        return;
      }
      const message = error instanceof Error ? error.message : this.dataset.errorText;
      if (errorText instanceof HTMLElement) {
        errorText.textContent = message || '';
        errorText.hidden = false;
      }
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      for (const { control, disabled } of disabledStates) {
        if (!disabled) control.removeAttribute('disabled');
      }
      for (const { element, inert } of inertStates) {
        if (!inert) element.removeAttribute('inert');
      }
      this.removeAttribute('aria-busy');
      this.querySelector('[data-button-label]')?.removeAttribute('hidden');
      this.querySelector('[data-button-pending]')?.setAttribute('hidden', '');
      SoleneCartMaskOffer.#pending = false;
    }
  };
}

if (!customElements.get('solene-cart-mask-offer')) {
  customElements.define('solene-cart-mask-offer', SoleneCartMaskOffer);
}
